import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Award, Copy, Check, ExternalLink, Code2, Globe, BarChart3, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const BADGE_IMG_PATH = "/assets/localbest-verified-badge.png";

/**
 * Dashboard — Website Badge tab.
 * Lets the business owner preview their LocalBest Verified Badge™, copy
 * the HTML or iframe embed code, and view click activity.
 */
export default function WebsiteBadgeTab({ slug }) {
  const { getAuthHeader } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [copied, setCopied] = useState("");

  const origin = (typeof window !== "undefined" && window.location.origin) || "https://localbest.ai";
  const listingUrl = `${origin}/business/${slug}`;
  const badgeUrl = `${origin}/badge/${slug}`;
  const embedUrl = `${origin}/embed/badge/${slug}`;
  const badgeAssetUrl = `${origin}${BADGE_IMG_PATH}`;

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API_V1}/badge/${slug}/analytics`, { headers: getAuthHeader() })
      .then((r) => setAnalytics(r.data))
      .catch(() => setAnalytics(null));
  }, [slug]);

  const htmlEmbed = `<a href="${badgeUrl}" target="_blank" rel="noopener noreferrer">
  <img
    src="${badgeAssetUrl}"
    alt="LocalBest Verified Badge"
    title="Visit My Verified LocalBest.ai Page"
    style="max-width:100%; width:360px; height:auto; border:0; display:block;"
  />
</a>`;

  const iframeEmbed = `<iframe
  src="${embedUrl}"
  width="380"
  height="150"
  style="border:0; overflow:hidden; max-width:100%;"
  loading="lazy"
  title="LocalBest Verified Badge"></iframe>`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(""), 1800);
    } catch (_) { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-6" data-testid="website-badge-tab">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#EC4899] flex items-center justify-center flex-shrink-0">
          <Award className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white font-['Outfit']">LocalBest Verified Badge™</h2>
          <p className="text-sm text-[#94A3B8] mt-1 max-w-2xl">
            Add your LocalBest Verified Badge™ to your website footer, homepage, About page, Contact page, service pages, or trust section. When visitors click the badge, they'll be taken to your LocalBest.ai verified listing page.
          </p>
        </div>
      </div>

      {analytics && !analytics.active && (
        <div className="rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 p-3 text-sm text-[#FCA5A5] flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>This LocalBest Verified Badge is not currently active. Once your listing is approved/published, the badge will activate automatically.</span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 items-start">
        {/* Badge Preview */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6 flex items-center justify-center min-h-[180px]" data-testid="badge-preview">
          <a href={listingUrl} target="_blank" rel="noopener noreferrer" className="block max-w-full">
            <img
              src={BADGE_IMG_PATH}
              alt="LocalBest Verified Badge"
              title="Visit My Verified LocalBest.ai Page"
              className="max-w-[320px] w-full h-auto block"
            />
          </a>
        </div>

        {/* Quick URLs */}
        <div className="space-y-3">
          <UrlRow label="Public Listing URL" url={listingUrl} onCopy={() => copy(listingUrl, "Listing URL")} copied={copied === "Listing URL"} testid="copy-listing-url-btn" />
          <UrlRow label="Badge Tracking URL" url={badgeUrl} onCopy={() => copy(badgeUrl, "Tracking URL")} copied={copied === "Tracking URL"} testid="copy-tracking-url-btn" />
          <UrlRow label="Iframe Embed URL" url={embedUrl} onCopy={() => copy(embedUrl, "Embed URL")} copied={copied === "Embed URL"} testid="copy-embed-url-btn" />
        </div>
      </div>

      {/* HTML embed */}
      <div className="rounded-xl bg-[#0F172A] border border-white/10 p-5" data-testid="html-embed-card">
        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Code2 className="w-4 h-4 text-[#10B981]" />
            <h3 className="text-white font-semibold">Option A — HTML Embed (Recommended)</h3>
          </div>
          <Button
            onClick={() => copy(htmlEmbed, "HTML embed")}
            size="sm"
            className={copied === "HTML embed" ? "bg-[#10B981] text-white" : "bg-[#10B981] hover:bg-[#059669] text-white"}
            data-testid="copy-html-embed-btn"
          >
            {copied === "HTML embed" ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy HTML</>}
          </Button>
        </div>
        <p className="text-xs text-[#94A3B8] mb-3">Best for most websites — clean SEO backlink, works in WordPress, Wix, Squarespace, Webflow, GoDaddy, custom HTML, and most page builders.</p>
        <pre className="text-xs text-[#E2E8F0] bg-black/40 rounded-md p-3 overflow-x-auto whitespace-pre"><code>{htmlEmbed}</code></pre>
      </div>

      {/* Iframe embed */}
      <div className="rounded-xl bg-[#0F172A] border border-white/10 p-5" data-testid="iframe-embed-card">
        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#7C3AED]" />
            <h3 className="text-white font-semibold">Option B — Iframe Embed</h3>
          </div>
          <Button
            onClick={() => copy(iframeEmbed, "Iframe embed")}
            size="sm"
            variant="outline"
            className="border-white/10 bg-white/5 hover:bg-white/10 text-white"
            data-testid="copy-iframe-embed-btn"
          >
            {copied === "Iframe embed" ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy Iframe</>}
          </Button>
        </div>
        <p className="text-xs text-[#94A3B8] mb-3">Use this if your site builder doesn't support raw HTML/image tags.</p>
        <pre className="text-xs text-[#E2E8F0] bg-black/40 rounded-md p-3 overflow-x-auto whitespace-pre"><code>{iframeEmbed}</code></pre>
      </div>

      {/* Placement tips */}
      <div className="rounded-xl bg-white/5 border border-white/10 p-5">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Info className="w-4 h-4 text-[#00E5FF]" /> Where to place your badge</h3>
        <ul className="text-sm text-[#94A3B8] space-y-1.5 list-disc list-inside marker:text-[#00E5FF]">
          <li>Website footer</li>
          <li>Homepage trust section</li>
          <li>About page</li>
          <li>Contact page</li>
          <li>Service pages</li>
          <li>Blog sidebar</li>
          <li>Awards or certifications section</li>
        </ul>
        <p className="text-xs text-[#64748B] mt-3 italic">For best results, place your badge near your reviews, awards, certifications, contact information, or trust badges.</p>
      </div>

      {/* Analytics */}
      <div className="rounded-xl bg-[#0F172A] border border-white/10 p-5" data-testid="badge-analytics">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#00E5FF]" /> Badge Activity</h3>
          {analytics?.active === false && <span className="text-xs px-2 py-0.5 rounded-full bg-[#EF4444]/15 text-[#FCA5A5]">Inactive</span>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Total Clicks" value={analytics?.total_clicks ?? "—"} />
          <Stat label="Last 30 days" value={analytics?.clicks_last_30d ?? "—"} />
          <Stat label="Last Click" value={analytics?.last_click_at ? new Date(analytics.last_click_at).toLocaleDateString() : "—"} />
          <Stat label="Top Referrer" value={analytics?.top_referrers?.[0]?.domain || "—"} />
        </div>
        {analytics?.top_referrers && analytics.top_referrers.length > 0 && (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wider text-[#64748B] mb-2">Top referring websites</p>
            <ul className="space-y-1">
              {analytics.top_referrers.slice(0, 5).map((r) => (
                <li key={r.domain} className="flex items-center justify-between text-sm">
                  <span className="text-[#E2E8F0] truncate">{r.domain}</span>
                  <span className="text-[#94A3B8] font-mono">{r.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <a
        href={listingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#00E5FF]/15 text-[#00E5FF] text-sm hover:bg-[#00E5FF]/25 transition"
        data-testid="badge-preview-listing-link"
      >
        Preview my listing <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}

function UrlRow({ label, url, onCopy, copied, testid }) {
  return (
    <div className="rounded-lg bg-[#0F172A] border border-white/10 p-3">
      <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <code className="text-xs text-[#E2E8F0] bg-black/40 rounded px-2 py-1 flex-1 truncate font-mono">{url}</code>
        <Button onClick={onCopy} size="icon" variant="outline" className="border-white/10 bg-white/5 hover:bg-white/10 text-white w-8 h-8" data-testid={testid}>
          {copied ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-3">
      <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">{label}</p>
      <p className="text-white font-bold text-lg truncate">{value}</p>
    </div>
  );
}
